import { createContext, useCallback, useContext, useEffect, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import type { CatalogRecord } from '@svetoch/contracts';
import { Icon, Modal } from '@svetoch/ui';
import { readPreference, writePreference } from '../shared/storage';

export type OpenRecord = Pick<CatalogRecord, 'id' | 'title' | 'systemNumber'>;
type Notice = { title: string; body: ReactNode; locationKey: string };
type Selection = Record<string, boolean>;
type WorkspaceValue = {
  openRecords: OpenRecord[];
  openRecord: (record: OpenRecord) => void;
  rememberRecord: (record: OpenRecord) => void;
  closeRecord: (id: string) => void;
  closeAllRecords: () => void;
  catalogLocation: string;
  selection: Selection;
  setSelection: Dispatch<SetStateAction<Selection>>;
  showNotice: (title: string, body: ReactNode) => void;
  notify: (message: string) => void;
  closeNotice: () => void;
  requestComparison: () => void;
  clearComparisonRequest: () => void;
  comparisonRequest: number;
};
const WorkspaceContext = createContext<WorkspaceValue | null>(null);
const catalogPath = '/catalog/bibliographic';
const initialRecords: OpenRecord[] = [
  { id: 'RSL01-015317247', systemNumber: 'RSL01-015317247', title: 'Весна' },
  { id: 'RSL01-015317216', systemNumber: 'RSL01-015317216', title: 'Соловецкие острова' }
];

function metadata(record: OpenRecord): OpenRecord {
  return { id: record.id, title: record.title, systemNumber: record.systemNumber };
}
function updateOpenRecord(current: OpenRecord[], record: OpenRecord, append = false) {
  const existing = current.find((item) => item.id === record.id);
  if (!existing) return append ? [...current, metadata(record)] : current;
  if (existing.title === record.title && existing.systemNumber === record.systemNumber) return current;
  return current.map((item) => item.id === record.id ? metadata(record) : item);
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [openRecords, setOpenRecords] = useState<OpenRecord[]>(() => {
    const saved = readPreference<unknown>('open-records', initialRecords);
    return Array.isArray(saved) && saved.every((r) => r && typeof r.id === 'string' && typeof r.systemNumber === 'string' && (typeof r.title === 'string' || r.title === null)) ? saved.map(metadata) : initialRecords;
  });
  const [notice, setNotice] = useState<Notice | null>(null);
  const [toast, setToast] = useState('');
  const [comparisonRequest, setComparisonRequest] = useState(0);
  const [selection, setSelection] = useState<Selection>({ 'RSL01-015317247': true });
  const [catalogLocation, setCatalogLocation] = useState(catalogPath);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [location.pathname]);
  useEffect(() => {
    setNotice((current) => current && current.locationKey !== location.key ? null : current);
  }, [location.key]);
  useEffect(() => {
    if (location.pathname === catalogPath) setCatalogLocation(location.pathname + location.search);
  }, [location.pathname, location.search]);
  useEffect(() => writePreference('open-records', openRecords), [openRecords]);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => queryClient.getQueryCache().subscribe((event) => {
    const query = event.query;
    if (event.type === 'updated' && query.queryKey[0] === 'record' && query.state.status === 'success' && query.state.data) {
      setOpenRecords((current) => updateOpenRecord(current, query.state.data as CatalogRecord));
    }
  }), [queryClient]);

  const notify = useCallback((message: string) => {
    setToast(message);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(''), 4200);
  }, []);
  const rememberRecord = useCallback((record: OpenRecord) => {
    setOpenRecords((current) => updateOpenRecord(current, record, true));
  }, []);
  const openRecord = useCallback((record: OpenRecord) => {
    rememberRecord(record);
    navigate(catalogPath + '/' + encodeURIComponent(record.id));
  }, [rememberRecord, navigate]);
  const closeRecord = useCallback((id: string) => {
    setOpenRecords((current) => current.filter((record) => record.id !== id));
    if (location.pathname === catalogPath + '/' + encodeURIComponent(id)) navigate(catalogLocation);
  }, [location.pathname, navigate, catalogLocation]);
  const closeAllRecords = useCallback(() => {
    setOpenRecords([]);
    if (location.pathname.startsWith(catalogPath + '/')) navigate(catalogLocation);
  }, [location.pathname, navigate, catalogLocation]);
  const requestComparison = useCallback(() => {
    setComparisonRequest((value) => value + 1);
    navigate(catalogLocation);
  }, [navigate, catalogLocation]);
  const clearComparisonRequest = useCallback(() => setComparisonRequest(0), []);
  const showNotice = useCallback((title: string, body: ReactNode) => setNotice({ title, body, locationKey: location.key }), [location.key]);
  const closeNotice = useCallback(() => setNotice(null), []);
  const hasSelectionToolbar = location.pathname === catalogPath && Object.values(selection).filter(Boolean).length > 1;

  return <WorkspaceContext.Provider value={{
    openRecords, openRecord, rememberRecord, closeRecord, closeAllRecords, catalogLocation,
    selection, setSelection, showNotice, closeNotice, notify,
    requestComparison, clearComparisonRequest, comparisonRequest
  }}>
    {children}
    <Modal open={!!notice} onOpenChange={(open) => !open && setNotice(null)} title={notice?.title || ''}>
      <div className="prose">{notice?.body}</div>
    </Modal>
    {toast && <div className={'toast' + (hasSelectionToolbar ? ' toast--above-selection' : '')} role="status"><Icon name="check-circle" /><span>{toast}</span><button aria-label="Закрыть уведомление" onClick={() => setToast('')}><Icon name="page-close" /></button></div>}
  </WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error('WorkspaceProvider is missing');
  return value;
}
